import { useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import {
  FilterWrapper,
  FilterRow,
  FolderPill,
  FolderPillEdit,
  CategoryChip,
  ExpandSearch,
  SearchInput,
  SearchMorphIcon,
  IconButton,
  SelectCounter,
} from "./CollectionFilter.styles";

import { ReactComponent as FavoritesIcon } from "../../assets/icons/favorites.svg";
import { ReactComponent as PlusCircleIcon } from "../../assets/icons/plus-circle.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";

import LottieIcon from "../LottieIcon/LottieIcon";
import checkboxAnimation from "../../assets/animations/checkBox.json";
import Dropdown from "../Dropdown/Dropdown";

import { ALLOWED_TAGS } from "../../utils/constants";

import {
  selectCategories,
  selectExploreCollectionsKeyword,
} from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";

const CollectionFilter = ({
  onSearchChange,
  filter,
  loading,
  minHeight,
  searchValue,
  setFilter,
  hideFilter,
  hideSearch = false,
  searchAlwaysOpen = false,
  searchPlaceholderText,
  openCreateFolderModal,
  folders,
  showFolders,
  openDeleteFolder,
  showSelect,
  setSelectedDeleteFolder,
  setSelect,
  select,
  selectedCollections,
  offHorizontalPadding,
  hidePicturesCategory,
  onChangeKeyword,
  isProEnabled,
  isExtendModal,
  extendData,
  collectionPage,
  searchRequiresAuth = false,
  onUnauthorizedSearch,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const { data: categories } = useSelector(selectCategories);
  const loggedIn = useSelector(selectLoggedIn);
  const savedKeyword = useSelector(selectExploreCollectionsKeyword);
  const isCompactMobile = useMediaQuery({ maxWidth: 480 });

  const inputRef = useRef();

  const isSearchLocked = searchRequiresAuth && !loggedIn;
  const isSearchExpanded =
    !hideSearch &&
    !isSearchLocked &&
    (searchAlwaysOpen || searchOpen || Boolean((searchValue ?? "").trim()));

  /* ── Derived values ─────────────────────────────────────────────────── */

  const visibleCategories = useMemo(() => {
    if (!categories) return [];
    return hidePicturesCategory
      ? categories.filter((c) => c.name !== "Picture")
      : categories;
  }, [categories, hidePicturesCategory]);

  const tagOptions = useMemo(
    () => [{ label: "All", id: 0 }, ...(ALLOWED_TAGS || [])],
    [],
  );

  const selectedTag = savedKeyword || "All";

  /* ── Handlers ────────────────────────────────────────────────────────── */

  const handleCategorySelect = (cat) => {
    if (!cat) return;
    searchParams.delete("page");
    setSearchParams(searchParams);
    setFilter(cat._id);
  };

  const handleTagChange = (tagLabel) => {
    if (onChangeKeyword) onChangeKeyword(tagLabel === "All" ? "" : tagLabel);
  };

  const handleFavoritesClick = () => {
    searchParams.delete("page");
    setSearchParams(searchParams);
    if (filter === "FAVORITES") {
      if (showFolders) {
        setFilter("ALL");
      } else {
        const trending = categories?.find((c) => c.name === "Trending");
        const fallback = trending?._id ?? categories?.[0]?._id;
        if (fallback) setFilter(fallback);
      }
    } else {
      setFilter("FAVORITES");
    }
  };

  const handleSearchToggle = () => {
    if (isSearchLocked) {
      onUnauthorizedSearch?.();
      return;
    }

    if (isSearchExpanded) {
      if (Boolean((searchValue ?? "").trim())) onSearchChange("");
      setSearchOpen(false);
      inputRef.current?.blur();
    } else {
      setSearchOpen(true);
    }
  };

  // Auto-focus when the search bar expands via the toggle.
  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (isSearchLocked && searchOpen) {
      setSearchOpen(false);
      inputRef.current?.blur();
    }
  }, [isSearchLocked, searchOpen]);

  const handleExtendClick = () => {
    setFilter("EXTEND_WITH");
  };

  /* ── Render ──────────────────────────────────────────────────────────── */

  const showCategoriesRow = !hideFilter;
  const showTagsRow = !hideFilter;
  const showFilterRow = showCategoriesRow || (loggedIn && showFolders);

  const categoryLoading = loading && (!categories || categories.length === 0);

  return (
    <FilterWrapper
      data-collection-filter
      $offHorizontalPadding={offHorizontalPadding}
      $minHeight={minHeight}
      $collectionPage={collectionPage}
    >
      {showFilterRow && (
        <FilterRow aria-label="Collection filters" role="region">
          {showCategoriesRow && categoryLoading ? (
            <>
              <CategoryChip
                $animation
                $width={44}
                style={{ borderRadius: "50%" }}
              />
              <CategoryChip
                $animation
                $width={44}
                style={{ borderRadius: "50%" }}
              />
              <CategoryChip $animation $width={96} />
              <CategoryChip $animation $width={100} />
              <CategoryChip $animation $width={72.48} />
              <CategoryChip $animation $width={141.15} />
              <CategoryChip $animation $width={108.77} />
            </>
          ) : (
            <>
              {!hideSearch && (
                <ExpandSearch
                  $expanded={isSearchExpanded}
                  $focused={searchFocused}
                >
                  <SearchMorphIcon
                    type="button"
                    onClick={handleSearchToggle}
                    $active={isSearchExpanded}
                    aria-label={
                      isSearchLocked
                        ? "Sign in to search"
                        : isSearchExpanded
                          ? "Close search"
                          : "Open search"
                    }
                    aria-pressed={isSearchExpanded}
                    aria-disabled={isSearchLocked}
                  >
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        className="icon-search"
                        pathLength="1"
                        d="m20 20-4.6-4.6m0 0A7 7 0 1 0 5.5 5.5a7 7 0 0 0 9.9 9.9Z"
                      />
                      <path
                        className="icon-x icon-x-1"
                        pathLength="1"
                        d="M7 7l10 10"
                      />
                      <path
                        className="icon-x icon-x-2"
                        pathLength="1"
                        d="M17 7L7 17"
                      />
                    </svg>
                  </SearchMorphIcon>
                  <SearchInput
                    ref={inputRef}
                    value={searchValue || ""}
                    placeholder={
                      searchPlaceholderText ?? "Search by title, tag…"
                    }
                    disabled={isSearchLocked}
                    onFocus={() => {
                      if (isSearchLocked) {
                        onUnauthorizedSearch?.();
                        return;
                      }
                      setSearchFocused(true);
                    }}
                    onBlur={() => setSearchFocused(false)}
                    onChange={(e) => {
                      if (isSearchLocked) {
                        onUnauthorizedSearch?.();
                        return;
                      }
                      onSearchChange(e.target.value);
                    }}
                    tabIndex={isSearchExpanded ? 0 : -1}
                    aria-hidden={!isSearchExpanded}
                  />
                </ExpandSearch>
              )}

              {loggedIn && (showCategoriesRow || showFolders) && (
                <IconButton
                  $active={filter === "FAVORITES"}
                  onClick={handleFavoritesClick}
                  aria-label="Favorites"
                  aria-pressed={filter === "FAVORITES"}
                  type="button"
                >
                  <FavoritesIcon />
                </IconButton>
              )}

              {showTagsRow && (
                <Dropdown
                  options={tagOptions}
                  placeholder="All tags"
                  defaultValue={selectedTag}
                  onChange={handleTagChange}
                  isProEnabled={isProEnabled}
                />
              )}

              {showCategoriesRow && isExtendModal && extendData && (
                <CategoryChip
                  $selected={filter === "EXTEND_WITH"}
                  onClick={handleExtendClick}
                >
                  Extend With
                </CategoryChip>
              )}

              {showCategoriesRow &&
                visibleCategories.map((cat) => (
                  <CategoryChip
                    key={cat._id}
                    $selected={filter === cat._id}
                    onClick={() => handleCategorySelect(cat)}
                  >
                    {cat.name}
                  </CategoryChip>
                ))}

              {loggedIn && collectionPage && showSelect && (
                <IconButton
                  $active={select}
                  onClick={() => setSelect(!select)}
                  aria-label="Toggle select mode"
                  aria-pressed={!!select}
                  type="button"
                >
                  {select ? (
                    <SelectCounter>
                      {selectedCollections?.length || 0}/20
                    </SelectCounter>
                  ) : (
                    <LottieIcon
                      animationData={checkboxAnimation}
                      speed={2}
                      trigger="toggle-reverse"
                      isActive={false}
                      size={isCompactMobile ? 22 : 24}
                    />
                  )}
                </IconButton>
              )}

              {loggedIn && showFolders && (
                <>
                  <FolderPill
                    $create
                    onClick={openCreateFolderModal}
                    aria-label="Create new folder"
                  >
                    <PlusCircleIcon />
                    New folder
                  </FolderPill>
                  <FolderPill
                    $selected={filter === "ALL" || !filter}
                    onClick={() => setFilter("ALL")}
                  >
                    All
                  </FolderPill>
                  {folders?.map((f) => (
                    <FolderPill
                      key={f._id}
                      $selected={filter === f._id}
                      onClick={() => setFilter(f._id)}
                    >
                      {f.name}
                      <FolderPillEdit
                        $selected={filter === f._id}
                        aria-label={`Edit folder ${f.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDeleteFolder?.(f);
                          openDeleteFolder?.();
                        }}
                      >
                        <EditIcon />
                      </FolderPillEdit>
                    </FolderPill>
                  ))}
                </>
              )}
            </>
          )}
        </FilterRow>
      )}
    </FilterWrapper>
  );
};

export default CollectionFilter;
